import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData } from '../../../apiService';
import { useLocation } from 'react-router-dom';

const SaleReturnDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, permissions, token, logout, branchstate } = useAuth();
    const [formData, setFormData] = useState({});
    const [selectedItems, setSelectedItems] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [SaleDraftId, setSaleDraftId] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const location = useLocation();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, branchId, userId, token, logout]);

    useEffect(() => {
        fetchData('SaleReturnApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.Sale);
                    setSaleDraftId(response.response_data.Sale.Id);
                    setSelectedItems(response.response_data.SaleDetails);
                }
            })
            .catch((error) => {
                console.error('Error fetching Sale Return Details:', error);
            });
    }, [id]);

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';
        if (location.state?.from === '/SaleReturnIndex' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
    }, [location]);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const filteredData = selectedItems.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    // ── Color tokens ──
    const GN  = "#1a7a4a";
    const GNL = "#e8f5ee";
    const GND = "#155d38";

    // ── Shared input styles (read-only) ──
    const inp = {
        border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "12px",
        height: "30px", width: "100%", padding: "0 8px", outline: "none",
        backgroundColor: "#f8fafa", boxSizing: "border-box", color: "#1a2e2e", cursor: "default"
    };
    const inpR = { ...inp, textAlign: "right" };
    const lbl = {
        fontSize: "10px", color: "#6b7280", display: "block",
        marginBottom: "2px", fontWeight: 500
    };

    // ── Section styles ──
    const sectionBox  = { backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "6px", marginBottom: "8px", overflow: "hidden" };
    const sectionHead = { backgroundColor: GNL, borderBottom: `2px solid ${GN}`, padding: "5px 12px" };
    const sectionTitle = { fontSize: "11px", fontWeight: 700, color: GND, textTransform: "uppercase", letterSpacing: "0.5px" };

    return (
        <div style={{ fontFamily: "Segoe UI, sans-serif", fontSize: "12px", width: "100%", padding: "6px", boxSizing: "border-box", backgroundColor: "#f5f5f5" }}>

            {/* ── Top action bar ── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", gap: "8px" }}>
                <span style={{ fontWeight: 700, fontSize: "13px", color: GN }}>Sale Return Details</span>
                <div style={{ display: "flex", gap: "8px" }}>
                    <Link
                        to="/SaleReturnIndex"
                        onClick={() => localStorage.setItem("shouldReload", "true")}
                        style={{ height: "30px", padding: "0 14px", borderRadius: "4px", border: "1px solid #d1d5db", backgroundColor: "#fff", color: "#374151", fontWeight: 600, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", textDecoration: "none" }}
                    >
                        ← Back
                    </Link>
                </div>
            </div>

            {errorMessage && <p style={{ color: "#ef4444", marginBottom: "8px", fontSize: "12px" }}>{errorMessage}</p>}

            {/* ══ SECTION 1 — Sale Return Information ══ */}
            <div style={sectionBox}>
                <div style={sectionHead}><span style={sectionTitle}>Sale Return Information</span></div>
                <div style={{ padding: "8px 12px", display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
                    {/* Customer */}
                    <div style={{ flex: "1 1 200px" }}>
                        <label style={lbl}>Customer</label>
                        <input type="text" value={formData.CustomerName || ""} readOnly style={inp} />
                    </div>
                    {/* Invoice Type */}
                    <div style={{ flex: "0 0 140px" }}>
                        <label style={lbl}>Invoice Type</label>
                        <input type="text" value={formData.InvoiceTypeName || ""} readOnly style={inp} />
                    </div>
                    {/* Sale Bill No */}
                    <div style={{ flex: "0 0 130px" }}>
                        <label style={lbl}>Sale Bill No</label>
                        <input type="text" value={formData.SaleBillNumber || ""} readOnly style={inp} />
                    </div>
                    {/* Sale Bill Date */}
                    <div style={{ flex: "0 0 130px" }}>
                        <label style={lbl}>Sale Bill Date</label>
                        <input type="date" value={formatDateForInput(formData.SaleBillDate)} readOnly style={inp} />
                    </div>
                    {/* Sale RTN Date */}
                    <div style={{ flex: "0 0 130px" }}>
                        <label style={lbl}>Sale RTN Date</label>
                        <input type="date" value={formatDateForInput(formData.BillDate)} readOnly style={inp} />
                    </div>
                    {/* Sale RTN Number */}
                    <div style={{ flex: "0 0 140px" }}>
                        <label style={lbl}>Sale RTN Number</label>
                        <input type="text" value={`${formData.BillPrefix || ''}/${formData.BillNumber || ''}/${formData.FinYear || ''}`} readOnly style={inp} />
                    </div>
                    {/* Store Location */}
                    <div style={{ flex: "0 0 180px" }}>
                        <label style={lbl}>Store Location</label>
                        <input type="text" value={formData.StoreLocationName || ""} readOnly style={inp} />
                    </div>
                </div>
            </div>

            {/* ══ SECTION 2 — Sale Return Items ══ */}
            <div style={sectionBox}>
                <div style={{ ...sectionHead, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
                    <span style={sectionTitle}>Sale Return Items ({filteredData.length} items)</span>
                    <input
                        type="text"
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ height: "24px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "11px", padding: "0 8px", outline: "none", width: "160px" }}
                    />
                </div>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                    {filteredData.length === 0 ? (
                        <div style={{ backgroundColor: GNL, padding: "20px", textAlign: "center", fontSize: "12px", color: GND }}>No items found</div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                            <thead style={{ backgroundColor: GN, color: "#fff", position: "sticky", top: 0 }}>
                                <tr>
                                    {["#", "Item", "S.Qty", "R.Qty", "Rate", "Amount", "Disc%", "Disc Amt", "Taxable", "GST%", "GST Amt", "Total"].map((h, i) => (
                                        <th key={i} style={{ padding: "6px 4px", textAlign: i > 1 ? "right" : "left", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((item, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : GNL, borderBottom: "1px solid #e5e7eb" }}>
                                        <td style={{ padding: "3px 6px", textAlign: "center", whiteSpace: "nowrap" }}>{index + 1}</td>
                                        <td style={{ padding: "3px 4px", minWidth: "160px", fontWeight: 600 }}>{item.ItemName}</td>
                                        <td style={{ padding: "2px", width: "70px" }}>
                                            <input type="number" value={item.SaleQuantity || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "70px" }}>
                                            <input type="number" value={item.Quantity || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "100px" }}>
                                            <input type="number" value={item.Rate || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "100px" }}>
                                            <input value={item.Amount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "80px" }}>
                                            <input type="number" value={item.DiscountPercentage || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "90px" }}>
                                            <input value={item.DiscountAmount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "100px" }}>
                                            <input value={item.TaxableAmount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "3px 4px", textAlign: "right", whiteSpace: "nowrap", fontSize: "11px", color: "#6b7280" }}>
                                            {item.GSTRate ? parseFloat(item.GSTRate) : 0}%
                                        </td>
                                        <td style={{ padding: "2px", width: "90px" }}>
                                            <input value={(parseFloat(item.CGSTAmount || 0) + parseFloat(item.SGSTAmount || 0) + parseFloat(item.IGSTAmount || 0)).toFixed(2)} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                        </td>
                                        <td style={{ padding: "2px", width: "100px" }}>
                                            <input value={item.TotalAmount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px", fontWeight: 700, color: GND }} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* ══ SECTION 3 — Bottom: Additional Charges + Summary ══ */}
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                {/* Left: Additional Charges */}
                <div style={{ ...sectionBox, flex: "1 1 0", marginBottom: 0 }}>
                    <div style={sectionHead}><span style={sectionTitle}>Additional Charges</span></div>
                    <div style={{ padding: "10px 12px" }}>
                        {/* Row 1: Freight, Packing, TCS, Cash, UPI, Card, Credit Amount */}
                        <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>Freight <span style={{ color: "#6b7280" }}>({formData.FreightGSTRate}% GST: {formData.FreightGSTAmount})</span></label>
                                <input type="number" value={formData.FreightAmount || 0} readOnly style={inpR} />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>Packing <span style={{ color: "#6b7280" }}>({formData.PackingGSTRate}% GST: {formData.PackingGSTAmount})</span></label>
                                <input type="number" value={formData.PackingAmount || 0} readOnly style={inpR} />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>TCS Amount</label>
                                <input type="number" value={formData.TCSAmount || 0} readOnly style={inpR} />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>Cash Amount</label>
                                <input type="number" value={formData.CashAmount || 0} readOnly style={{ ...inpR, fontWeight: 700, color: "#059669" }} />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>UPI Amount</label>
                                <input type="number" value={formData.UPIAmount || 0} readOnly style={{ ...inpR, fontWeight: 700, color: "#059669" }} />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>Card Amount</label>
                                <input type="number" value={formData.CardAmount || 0} readOnly style={{ ...inpR, fontWeight: 700, color: "#059669" }} />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <label style={lbl}>Credit Amount</label>
                                <input type="text" value={formData.BalanceAmount || 0} readOnly
                                    style={{ ...inpR, fontWeight: 700, color: parseFloat(formData.BalanceAmount) > 0 ? "#ef4444" : "#1a2e2e", backgroundColor: parseFloat(formData.BalanceAmount) > 0 ? "#fef2f2" : "#f8fafa" }} />
                            </div>
                        </div>
                        {/* Row 2: Remarks, Bank */}
                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                            <div style={{ flex: "5 1 0" }}>
                                <label style={lbl}>Remarks</label>
                                <input type="text" value={formData.Remarks || ""} readOnly style={inp} />
                            </div>
                            {(formData.UPIAmount > 0 || formData.CardAmount > 0) && (
                                <div style={{ flex: "1 1 0" }}>
                                    <label style={lbl}>Bank</label>
                                    <input type="text" value={formData.BankName || ""} readOnly style={inp} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Right: Summary */}
                <div style={{ ...sectionBox, width: "200px", flexShrink: 0, marginBottom: 0 }}>
                    <div style={sectionHead}><span style={sectionTitle}>Summary</span></div>
                    <div style={{ padding: "8px 12px" }}>
                        {[
                            ["Subtotal", formData.TotalAmount, false],
                            ["Discount", formData.DiscountAmount, true],
                            ["Taxable", formData.TaxableAmount, false],
                            ["CGST", formData.CGSTAmount, false],
                            ["SGST", formData.SGSTAmount, false],
                            ["IGST", formData.IGSTAmount, false],
                            ["TCS", formData.TCSAmount, false],
                            ["Round Off", formData.RoundOff, false],
                        ].map(([label, val, isDiscount]) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #f3f4f6", fontSize: "11px" }}>
                                <span style={{ color: "#6b7280" }}>{label}</span>
                                <span style={{ color: isDiscount ? "#ef4444" : "#1a2e2e", fontWeight: 700, fontSize: "12px" }}>{isDiscount ? "- " : ""}₹{parseFloat(val || 0).toFixed(2)}</span>
                            </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0 2px 0", borderTop: `2px solid ${GN}`, marginTop: "2px" }}>
                            <span style={{ fontSize: "12px", fontWeight: 700, color: GND }}>Total</span>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: GN }}>₹{parseFloat(formData.GrandTotal || 0).toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SaleReturnDetails;
